/**
 * PendingAgreementsCallout · Feb 2026 · Iter 208
 * ─────────────────────────────────────────────────────────
 * Renders a prominent banner on both `/seller/dropship?tab=connect`
 * and `/buyer/dropship?tab=connect` surfacing draft dropship
 * agreements that need the CURRENT user's attention.
 *
 * Three buckets, three visual weights:
 *   awaiting_me   → amber, "Action required · Sign now"
 *   awaiting_them → indigo (low-emphasis), "Waiting on partner"
 *   unstarted     → grey  (informational),  "Start negotiating"
 *
 * Clicking any row deep-links to the same setup page scoped to the
 * counterpart via `?partner=<id>` so the inline agreement editor
 * (which handles the sign click) is one click away.
 */
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../../lib/api";
import { FileSignature, Clock, AlertCircle, ArrowRight, Loader2, Info } from "lucide-react";

// role: "seller" or "buyer" — controls deep-link targets.
export default function PendingAgreementsCallout({ role = "seller", onChanged }) {
  const [data, setData] = useState({ awaiting_me: [], awaiting_them: [], unstarted: [] });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(() => {
    setLoading(true);
    api.get("/dropship/pending-signatures")
      .then(r => setData(r.data || { awaiting_me: [], awaiting_them: [], unstarted: [] }))
      .catch(() => toast.error("Could not load pending agreements"))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openPartner = (cpId) => {
    // Same page — just scope to this counterpart. The parent tab already
    // syncs `partner` search params for the agreement editor.
    const next = new URLSearchParams(window.location.search);
    next.set("partner", cpId);
    next.set("tab", "connect");
    navigate(`?${next.toString()}`, { replace: true });
    onChanged?.();
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-dashed border-border bg-secondary/20 p-4 flex items-center gap-2 text-[12px] text-foreground/50"
           data-testid="pending-agreements-loading">
        <Loader2 className="w-3.5 h-3.5 animate-spin"/>
        Loading pending agreements…
      </div>
    );
  }

  const total = data.awaiting_me.length + data.awaiting_them.length + data.unstarted.length;
  if (total === 0) return null;

  return (
    <div className="rounded-2xl border border-border bg-white overflow-hidden"
         data-testid="pending-agreements-callout">
      <div className="px-5 py-3 border-b border-border flex items-center gap-2">
        <FileSignature className="w-3.5 h-3.5 text-foreground/60"/>
        <div className="text-[13px] font-semibold">Pending signatures</div>
        <div className="text-[11.5px] text-foreground/55">
          Dropship activates for a partner only after BOTH sides sign.
        </div>
        {data.awaiting_me.length > 0 && (
          <span className="ml-auto inline-flex items-center gap-1 text-[10.5px] mono uppercase tracking-[0.14em] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200"
                data-testid="pending-agreements-badge-action">
            <AlertCircle className="w-3 h-3"/>
            {data.awaiting_me.length} needs your signature
          </span>
        )}
      </div>

      {/* awaiting_me — top priority, amber. */}
      {data.awaiting_me.length > 0 && (
        <BucketList
          title="Action required · Sign to activate"
          rows={data.awaiting_me}
          tone="amber"
          icon={AlertCircle}
          role={role}
          onClick={openPartner}
          testIdPrefix="pending-awaiting-me"
        />
      )}
      {/* awaiting_them — informational. */}
      {data.awaiting_them.length > 0 && (
        <BucketList
          title="Waiting on partner"
          rows={data.awaiting_them}
          tone="indigo"
          icon={Clock}
          role={role}
          onClick={openPartner}
          testIdPrefix="pending-awaiting-them"
        />
      )}
      {/* unstarted — draft with no one signed yet. */}
      {data.unstarted.length > 0 && (
        <BucketList
          title="Drafts · Not yet signed"
          rows={data.unstarted}
          tone="grey"
          icon={Info}
          role={role}
          onClick={openPartner}
          testIdPrefix="pending-unstarted"
        />
      )}
    </div>
  );
}

function BucketList({ title, rows, tone, icon: Icon, role, onClick, testIdPrefix }) {
  const toneCls = {
    amber:  "text-amber-800 bg-amber-50/60",
    indigo: "text-indigo-800 bg-indigo-50/40",
    grey:   "text-foreground/70 bg-secondary/25",
  }[tone];
  return (
    <div className="border-b last:border-b-0 border-border">
      <div className={`px-5 py-2 flex items-center gap-2 text-[11px] mono uppercase tracking-[0.14em] ${toneCls}`}>
        <Icon className="w-3 h-3"/> {title}
      </div>
      <div className="divide-y divide-border">
        {rows.map(r => (
          <button
            key={r.counterpart_id}
            onClick={() => onClick(r.counterpart_id)}
            data-testid={`${testIdPrefix}-row-${r.counterpart_id}`}
            className="w-full flex items-center gap-3 px-5 py-3 hover:bg-secondary/40 text-left transition-colors"
          >
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-medium truncate">{r.counterpart_name}</div>
              <div className="text-[11px] text-foreground/55 truncate">
                {r.counterpart_email || `${role === "seller" ? "Buyer" : "Seller"} · draft v${r.version}`}
                {r.last_edited_at && ` · edited ${new Date(r.last_edited_at).toLocaleDateString()}`}
              </div>
            </div>
            <div className="text-[11px] mono uppercase tracking-[0.14em] text-foreground/50 shrink-0 inline-flex items-center gap-1">
              {tone === "amber" ? "Sign now" : tone === "indigo" ? "Awaiting them" : "Open"}
              <ArrowRight className="w-3 h-3"/>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
